import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { drawService } from '../../services/drawService';
import { Draw } from '../../types';
import { Icon } from '../../components/participant/Icon';
import { ParticipantTopBar } from '../../components/participant/ParticipantTopBar';
import { participantColors as c, fadeUpKeyframes } from '../../theme/participantTheme';

interface SecondaryAction {
  title: string;
  description: string;
  icon: string;
  path: string;
}

const secondaryActions: SecondaryAction[] = [
  { title: 'Ver campanhas', description: 'Descubra promoções disponíveis', icon: 'campaign', path: '/user/campaigns' },
  { title: 'Meus cupons', description: 'Acompanhe seus cupons cadastrados', icon: 'receipt_long', path: '/user/my-coupons' },
  { title: 'Sorteios', description: 'Resultados e próximos sorteios', icon: 'trophy', path: '/draws' },
];

const MONTHS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/**
 * Home do fluxo do participante (role USUARIO_FINAL) — hub de ações rápidas.
 * Renderizado por Dashboard.tsx quando user.role === 'USUARIO_FINAL'.
 */
export const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [nextDraw, setNextDraw] = useState<Draw | null>(null);

  useEffect(() => {
    let active = true;
    drawService
      .getAllDraws()
      .then((draws) => {
        if (!active) return;
        const upcoming = draws
          .filter((d) => d.status === 'PENDING')
          .sort((a, b) => new Date(a.drawDate).getTime() - new Date(b.drawDate).getTime());
        setNextDraw(upcoming[0] || null);
      })
      .catch(() => {
        if (active) setNextDraw(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const firstName = user?.name?.trim()?.split(' ')[0] || 'Participante';

  return (
    <Box sx={{ minHeight: '100vh', background: c.gradients.pageBg, fontFamily: c.fontFamily, pb: '48px' }}>
      <style>{fadeUpKeyframes}</style>

      <Box sx={{ maxWidth: 620, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <ParticipantTopBar showAvatarActions />

        {/* Saudação */}
        <Box sx={{ animation: 'participantFadeUp 0.35s ease both' }}>
          <Box component="div" sx={{ fontSize: 29, fontWeight: 800, color: c.textDarkest, lineHeight: 1.2 }}>
            Olá, {firstName}! 👋
          </Box>
          <Box component="div" sx={{ fontSize: 17, color: c.textSecondary, mt: '4px' }}>
            Participe de sorteios incríveis e gerencie seus cupons
          </Box>
        </Box>

        {/* CTA primário */}
        <Box
          component="button"
          onClick={() => navigate('/user/my-coupons/new')}
          sx={{
            width: '100%',
            border: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            borderRadius: `${c.radius.cardLarge}px`,
            background: c.gradients.primaryButton,
            padding: '26px 24px',
            boxShadow: c.shadows.primaryButton,
            display: 'flex',
            alignItems: 'center',
            gap: '18px',
            transition: 'transform 0.15s',
            animation: 'participantFadeUp 0.4s ease both',
            '&:hover': { transform: 'translateY(-3px)' },
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              flexShrink: 0,
              borderRadius: `${c.radius.tile}px`,
              background: 'rgba(255,255,255,0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="confirmation_number" size={36} color={c.onDarkPrimary} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box component="div" sx={{ fontSize: 22, fontWeight: 800, color: c.onDarkPrimary }}>
              Cadastrar cupom
            </Box>
            <Box component="div" sx={{ fontSize: 15.5, color: c.onDarkSecondary, mt: '2px' }}>
              Cadastre seus cupons de compra para participar dos sorteios
            </Box>
          </Box>
          <Icon name="chevron_right" size={28} color={c.onDarkPrimary} />
        </Box>

        {/* Banner próximo sorteio */}
        {nextDraw && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              background: c.amberBg,
              border: `2px solid ${c.amber}`,
              borderRadius: `${c.radius.card}px`,
              padding: '16px 18px',
              animation: 'participantFadeUp 0.45s ease both',
            }}
          >
            <Icon name="emoji_events" size={34} color={c.amber} />
            <Box sx={{ flex: 1 }}>
              <Box component="div" sx={{ fontSize: 13, fontWeight: 800, color: c.amberTextDark, textTransform: 'uppercase' }}>
                Próximo sorteio
              </Box>
              <Box component="div" sx={{ fontSize: 15.5, fontWeight: 700, color: c.amberTextDarker, mt: '2px' }}>
                {nextDraw.prize?.name || nextDraw.campaign?.name || 'Sorteio agendado'} —{' '}
                {new Date(nextDraw.drawDate).getDate()} de{' '}
                {MONTHS[new Date(nextDraw.drawDate).getMonth()].toLowerCase().replace(/^\w/, (ch) => ch.toUpperCase())}
              </Box>
            </Box>
          </Box>
        )}

        {/* Ações secundárias */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '14px',
            animation: 'participantFadeUp 0.5s ease both',
          }}
        >
          {secondaryActions.map((action) => (
            <Box
              key={action.path}
              component="button"
              onClick={() => navigate(action.path)}
              sx={{
                textAlign: 'left',
                cursor: 'pointer',
                background: c.surface,
                border: '2px solid transparent',
                borderRadius: `${c.radius.card}px`,
                padding: '20px',
                boxShadow: c.shadows.secondaryCard,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                transition: 'border-color 0.15s, transform 0.15s',
                '&:hover': { borderColor: c.brandMid, transform: 'translateY(-2px)' },
              }}
            >
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: `${c.radius.tile}px`,
                  background: c.surfaceTint,
                  color: c.brandDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name={action.icon} size={28} />
              </Box>
              <Box component="div" sx={{ fontSize: 18, fontWeight: 800, color: c.textDarkest }}>
                {action.title}
              </Box>
              <Box component="div" sx={{ fontSize: 14.5, color: c.textSecondary }}>
                {action.description}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', pt: '16px', borderTop: `1px solid ${c.border}` }}>
          <Box component="div" sx={{ fontSize: 13.5, color: c.textTertiary, fontWeight: 600 }}>
            Sistema de Sorteios — Associação Comercial © 2026
          </Box>
          <Box component="div" sx={{ fontSize: 12.5, color: c.textFaint, mt: '2px' }}>
            Portal do Participante
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
